import { CommentOutlined, HighlightOutlined } from "@ant-design/icons";
import { Button, Modal, Space, message } from "antd";
import { descriptionRules, nameRules } from "common/FormValidationRules";
import Tooltip from "components/atoms/Tooltip";
import DDLSqlExportModal from "components/molecules/DDLSqlExportModal";
import GapPredictionModal from "components/molecules/GapPredictionModal";
import ManageSchemaModelForm from "components/molecules/ManageSchemaModelForm";
import SchemaHealthReportModal from "components/molecules/SchemaHealthReportModal";
import FieldDescriptionsModal from "components/organisms/FieldDescriptionsModal";
import PIISuggestionsModal from "components/organisms/PIISuggestionsModal";
import Annotations from "containers/Annotations";
import Comments from "containers/Comments";
import _ from "lodash";
import React, { Component } from "react";
import { connect } from "react-redux";
import { RouteComponentProps } from "react-router-dom";
import actions from "store/actions";

interface ISchemaDataParam {
    schemaModel: Partial<ISchemaModel> | null; // Without fields
    newFields: ISchemaField[];
    deletedFields: ISchemaField[];
    updatedFields: ISchemaField[];
}

interface IMatchParams {
    tenant: string;
    businessarea: string;
}

type IResultStateWithLoading = IResultState & { loading?: boolean };

type IManageSchemaModelProps = {
    action: "view" | "edit" | "create";
    businessAreaId?: string;
    businessAreaDataList: IBusinessAreaData[];
    defaultValue?: ISchemaModel;
    userBusinessAreas?: Record<string, IBusinessArea>;
    schemaModel?: ISchemaModel;
    getCommentsPermissionResult: IResultState;
    getDataTagsByBusinessAreaIdResult: IResultState;
    dataTagsByBusinessAreaIdResult: { Items?: any } & IResultState;
    getBusinessAreaDataForBusinessAreaResult: IResultState;
    autoTaggingResult?: { error?: string; loading?: boolean } & IResultState;
    autoTaggingData?: any;
    formErrors: any;
    permission: IPermissionState;
    userCommentsPermission: IPermissionState;
    keyExists?: { exists?: boolean };
    generateSchemaHealthReportResult?: IResultStateWithLoading;
    schemaHealthReportHtml?: string;
    getPiiSuggestionsResult?: IResultStateWithLoading;
    piiSuggestionsData?: any;
    savePiiSuggestionsResult?: IResultStateWithLoading;
    getFieldDescriptionsResult?: IResultStateWithLoading;
    fieldDescriptionsData?: any;
    saveFieldDescriptionsResult?: IResultStateWithLoading;
    generateDdlSqlResult?: IResultStateWithLoading;
    ddlSqlData?: any;
    generateGapPredictionResult?: IResultStateWithLoading;
    gapPredictionData?: any;
    getCommentsPermission: () => void;
    updateSchemaModel: (schemaId: string, updateSchemaModelData: ISchemaDataParam, mappings: any, afterSaveCallback?: () => void) => void;
    createSchemaModel: (newSchema: ISchemaModel, afterSaveCallback?: () => void) => void;
    exportSchema: (schemaId: string) => void;
    autoTagging: (payload: any) => void;
    getDataTagsByBusinessAreaId: (businessAreaId: string) => void;
    generateSchemaHealthReport: (schemaId: string, businessAreaId: string, regenerate: boolean) => void;
    getPiiSuggestions: (schemaId: string, businessAreaId: string) => void;
    savePiiSuggestions: (businessAreaId: string, schemaId: string, updatedFields: any[]) => void;
    clearPiiSuggestions: () => void;
    getFieldDescriptions: (schemaId: string, businessAreaId: string) => void;
    saveFieldDescriptions: (businessAreaId: string, schemaId: string, updatedFields: any[], schemaDescription: string) => void;
    clearFieldDescriptions: () => void;
    generateDdlSql: (schemaId: string, businessAreaId: string) => void;
    clearDdlSql: () => void;
    generateGapPrediction: (schemaId: string, businessAreaId: string, regenerate?: boolean) => void;
    clearGapPrediction: () => void;
    onValuesChanged: (changedValue: Partial<ISchemaModel>, allValues: ISchemaModel) => void;
    onCancel: () => void;
    onSwitchEditMode: (schemaId: string) => void;
} & RouteComponentProps<IMatchParams>;

type IManageSchemaModelStates = {
    readOnlyView: boolean;
    showSaveDraftConfirmationModal: boolean;
    showSaveConfirmationModal: boolean;
    isAutoTagging: boolean;
    autoTagError: boolean;
    isCommentsModalVisible: boolean;
    isAnnotationsModalVisible: boolean;
    fetchingCommentsPermission: boolean;
    isFetchingTags: boolean;
    isAutoTagRequested: boolean;
    openFieldCommentId: string | null;
    openFieldAnnotationId: string | null;
    businessAreaId?: string;
    isHealthReportModalVisible: boolean;
    isPIISuggestionsModalVisible: boolean;
    isPiiSuggestionsSaveRequested: boolean;
    isFieldDescriptionsModalVisible: boolean;
    isFieldDescriptionsSaveRequested: boolean;
    isDdlSqlModalVisible: boolean;
    isGapPredictionModalVisible: boolean;
};

class ManageSchemaModel extends Component<IManageSchemaModelProps, IManageSchemaModelStates> {
    private emptyObject;
    private emptySchemaModel: ISchemaModel;

    private formComponentRef = React.createRef<any>();

    constructor(props: IManageSchemaModelProps) {
        super(props);
        this.state = {
            readOnlyView: this.props.action === "view",
            showSaveDraftConfirmationModal: false,
            showSaveConfirmationModal: false,
            isAutoTagging: false,
            autoTagError: false,
            isCommentsModalVisible: false,
            isAnnotationsModalVisible: false,
            fetchingCommentsPermission: true,
            isFetchingTags: false,
            isAutoTagRequested: false,
            openFieldCommentId: null,
            openFieldAnnotationId: null,
            businessAreaId:
                this.props.businessAreaId ||
                (this.props.match &&
                    this.props.match.params &&
                    this.props.match.params.businessarea &&
                    this.props.userBusinessAreas &&
                    this.props.userBusinessAreas[this.props.match.params.businessarea.toLowerCase()] &&
                    this.props.userBusinessAreas[this.props.match.params.businessarea.toLowerCase()].businessAreaId),
            isHealthReportModalVisible: false,
            isPIISuggestionsModalVisible: false,
            isPiiSuggestionsSaveRequested: false,
            isFieldDescriptionsModalVisible: false,
            isFieldDescriptionsSaveRequested: false,
            isDdlSqlModalVisible: false,
            isGapPredictionModalVisible: false,
        };
        this.emptyObject = {};
        this.emptySchemaModel = { name: "", description: "", schemaId: "", businessAreaId: "", fields: [] };
    }

    getCurrentSchemaModel = () => {
        return this.props.defaultValue || this.props.schemaModel || this.emptySchemaModel;
    };

    componentDidMount() {
        this.props.getCommentsPermission();
        const params = new URLSearchParams(this.props.location ? this.props.location.search : window.location.search);
        if (params.get("schema_comment") === "true") {
            this.setState({ isCommentsModalVisible: true });
        }
        if (params.get("schema_annotation") === "true") {
            this.setState({ isAnnotationsModalVisible: true });
        }
        const fieldId = params.get("fieldId");
        if (fieldId) {
            if (params.get("schemafield_comment") === "true") {
                this.setState({ openFieldCommentId: fieldId });
            }
            if (params.get("schemafield_annotation") === "true") {
                this.setState({ openFieldAnnotationId: fieldId });
            }
        }
    }

    componentDidUpdate(prevProps: IManageSchemaModelProps) {
        this.onGetCommentsPermissionResultChanged(prevProps);
        this.onPiiSuggestionsResultChanged(prevProps);
        this.onSavePiiSuggestionsResultChanged(prevProps);
        this.onFieldDescriptionsResultChanged(prevProps);
        this.onSaveFieldDescriptionsResultChanged(prevProps);
        this.onDdlSqlResultChanged(prevProps);
        this.onGapPredictionResultChanged(prevProps);
        this.onGetDataTagsByBusinessAreaIdResultChanged(prevProps);
        this.onAutoTaggingResultChanged(prevProps);
    }

    onGetDataTagsByBusinessAreaIdResultChanged = (prevProps: IManageSchemaModelProps) => {
        if (this.props.getDataTagsByBusinessAreaIdResult && this.props.getDataTagsByBusinessAreaIdResult !== prevProps.getDataTagsByBusinessAreaIdResult) {
            if ((this.props.getDataTagsByBusinessAreaIdResult as any).loading) {
                return;
            }
            this.setState({ isFetchingTags: false });
            if (!this.props.getDataTagsByBusinessAreaIdResult.success) {
                if (this.state.isAutoTagRequested) {
                    this.setState({ autoTagError: true, isAutoTagRequested: false });
                    message.error(this.props.getDataTagsByBusinessAreaIdResult.message || "Failed to load classifier tags. Please try again.");
                }
            } else {
                if (this.state.isAutoTagRequested) {
                    const updatedTags = (this.props.dataTagsByBusinessAreaIdResult as any)?.Items || [];
                    if (updatedTags.length === 0) {
                        this.formComponentRef.current?.showNoTagsWarning();
                    } else {
                        this.formComponentRef.current?.performAutoTagging(updatedTags);
                        this.setState({ isAutoTagRequested: false });
                    }
                }
            }
        }
    };

    onAutoTaggingResultChanged = (prevProps: IManageSchemaModelProps) => {
        if (this.props.autoTaggingResult && this.props.autoTaggingResult !== prevProps.autoTaggingResult) {
            this.setState({ isAutoTagging: false, isAutoTagRequested: false });
            if (this.props.autoTaggingResult.loading) {
                return;
            }
            if (this.props.autoTaggingResult.success === false || (this.props.autoTaggingData && this.props.autoTaggingData.status === "TIMEOUT")) {
                this.setState({ autoTagError: true });
            } else {
                this.setState({ autoTagError: false });
            }
        }
    };

    onGetCommentsPermissionResultChanged = (prevProps: IManageSchemaModelProps) => {
        if (this.props.getCommentsPermissionResult && this.props.getCommentsPermissionResult !== prevProps.getCommentsPermissionResult) {
            if (!this.props.getCommentsPermissionResult.success) {
                console.error("Could not retrieve comments permissions.");
            }
            this.setState({
                fetchingCommentsPermission: false,
            });
        }
    };

    onPiiSuggestionsResultChanged = (prevProps: IManageSchemaModelProps) => {
        if (this.props.getPiiSuggestionsResult && this.props.getPiiSuggestionsResult !== prevProps.getPiiSuggestionsResult) {
            if (!this.props.getPiiSuggestionsResult.loading && !this.props.getPiiSuggestionsResult.success) {
                const errorMsg = this.props.getPiiSuggestionsResult.message || "Unable to get PII suggestions at this moment.";
                message.error(errorMsg);
            }
        }
    };

    onSavePiiSuggestionsResultChanged = (prevProps: IManageSchemaModelProps) => {
        if (this.props.savePiiSuggestionsResult && this.props.savePiiSuggestionsResult !== prevProps.savePiiSuggestionsResult) {
            if (!this.props.savePiiSuggestionsResult.loading && this.state.isPiiSuggestionsSaveRequested) {
                if (this.props.savePiiSuggestionsResult.success) {
                    message.success("PII suggestions saved successfully.");
                    this.setState({ isPIISuggestionsModalVisible: false, isPiiSuggestionsSaveRequested: false });
                } else {
                    message.error(this.props.savePiiSuggestionsResult.message || "Failed to save PII suggestions.");
                    this.setState({ isPiiSuggestionsSaveRequested: false });
                }
            }
        }
    };

    onFieldDescriptionsResultChanged = (prevProps: IManageSchemaModelProps) => {
        if (this.props.getFieldDescriptionsResult && this.props.getFieldDescriptionsResult !== prevProps.getFieldDescriptionsResult) {
            if (!this.props.getFieldDescriptionsResult.loading && !this.props.getFieldDescriptionsResult.success) {
                const errorMsg = this.props.getFieldDescriptionsResult.message || "Unable to get field descriptions at this moment.";
                message.error(errorMsg);
            }
        }
    };

    onSaveFieldDescriptionsResultChanged = (prevProps: IManageSchemaModelProps) => {
        if (this.props.saveFieldDescriptionsResult && this.props.saveFieldDescriptionsResult !== prevProps.saveFieldDescriptionsResult) {
            if (!this.props.saveFieldDescriptionsResult.loading && this.state.isFieldDescriptionsSaveRequested) {
                if (this.props.saveFieldDescriptionsResult.success) {
                    message.success("Field descriptions saved successfully.");
                    this.setState({ isFieldDescriptionsModalVisible: false, isFieldDescriptionsSaveRequested: false });
                } else {
                    message.error(this.props.saveFieldDescriptionsResult.message || "Failed to save field descriptions.");
                    this.setState({ isFieldDescriptionsSaveRequested: false });
                }
            }
        }
    };

    onDdlSqlResultChanged = (prevProps: IManageSchemaModelProps) => {
        if (this.props.generateDdlSqlResult && this.props.generateDdlSqlResult !== prevProps.generateDdlSqlResult) {
            if (!this.props.generateDdlSqlResult.loading && !this.props.generateDdlSqlResult.success) {
                const errorMsg = this.props.generateDdlSqlResult.message || "Unable to generate DDL SQL at this moment.";
                message.error(errorMsg);
            }
        }
    };

    onGapPredictionResultChanged = (prevProps: IManageSchemaModelProps) => {
        if (this.props.generateGapPredictionResult && this.props.generateGapPredictionResult !== prevProps.generateGapPredictionResult) {
            if (!this.props.generateGapPredictionResult.loading && !this.props.generateGapPredictionResult.success) {
                const errorMsg = this.props.generateGapPredictionResult.message || "Unable to generate gap prediction at this moment.";
                message.error(errorMsg);
            }
        }
    };

    getSchemaRecordCount = () => {
        return Number((this.props.schemaModel as any)?.totalRecordCount || 0);
    };

    getPrimaryKeyFieldIds = (fields: ISchemaField[] = []) => {
        return fields
            .filter((field) => !!field?.isPrimaryKey)
            .map((field) => field.fieldId || `new:${field.name || ""}`)
            .sort();
    };

    getPrimaryKeyChangeType = (originalFields: ISchemaField[] = [], updatedFields: ISchemaField[] = []) => {
        const originalPrimaryKeyIds = this.getPrimaryKeyFieldIds(originalFields);
        const updatedPrimaryKeyIds = this.getPrimaryKeyFieldIds(updatedFields);

        if (_.isEqual(originalPrimaryKeyIds, updatedPrimaryKeyIds)) {
            return null;
        }

        if (updatedPrimaryKeyIds.length > originalPrimaryKeyIds.length) {
            return "ADD";
        }

        if (updatedPrimaryKeyIds.length < originalPrimaryKeyIds.length) {
            return "REMOVE";
        }

        return "CHANGE";
    };

    onFormSubmit = (editedSchemaModel: ISchemaModel, afterSaveCallback?: () => void) => {
        if (!this.isSaveAllowed()) {
            return;
        }

        if (this.props.action.toLowerCase() === "edit") {
            const updateSchemaModelData: ISchemaDataParam = {
                schemaModel: null,
                newFields: [],
                deletedFields: [],
                updatedFields: [],
            };

            const originalSchemaModel: Partial<ISchemaModel> = _.cloneDeep(this.props.schemaModel ?? {});
            const updatedSchemaModel: Partial<ISchemaModel> = _.cloneDeep(editedSchemaModel);

            const totalRecordCount = this.getSchemaRecordCount();
            const pkChangeType = this.getPrimaryKeyChangeType(originalSchemaModel.fields || [], updatedSchemaModel.fields || []);
            if (totalRecordCount > 0 && pkChangeType) {
                message.error(`Cannot ${pkChangeType.toLowerCase()} primary key field when schema has ${totalRecordCount} existing records. Remove all records first.`);
                return;
            }

            const originalSchemaFieldDict: Record<string, ISchemaField> = {};
            (originalSchemaModel.fields ?? []).forEach((field) => {
                originalSchemaFieldDict[field.fieldId] = field;
            });

            const updatedSchemaFieldDict: Record<string, ISchemaField> = {};
            const newFieldIndexMappings: Record<number, number> = {};
            const updatedSchemaFieldIndexes: Record<string, number> = {};
            const fieldNamesByIndex: Record<number, string> = {};

            if (updatedSchemaModel.fields && Array.isArray(updatedSchemaModel.fields)) {
                for (let index = 0; index < updatedSchemaModel.fields.length; index++) {
                    const field = _.cloneDeep(updatedSchemaModel.fields[index]);
                    fieldNamesByIndex[index] = field?.name || field?.description || `Field ${index + 1}`;
                    if (field.range) {
                        switch (field.dataType) {
                            case "datetime":
                            case "date":
                                field.min = field.range[0].format("YYYY-MM-DDTHH:mm:ss");
                                field.max = field.range[1].format("YYYY-MM-DDTHH:mm:ss");
                                delete field.range;
                                break;
                            case "time":
                                field.min = field.range[0].utc().valueOf();
                                field.max = field.range[1].utc().valueOf();
                                delete field.range;
                                break;
                            default:
                                break;
                        }
                    }

                    if (field.fieldId) {
                        updatedSchemaFieldDict[field.fieldId] = field;
                        updatedSchemaFieldIndexes[field.fieldId] = index;
                    } else {
                        newFieldIndexMappings[updateSchemaModelData.newFields.length] = index;
                        updateSchemaModelData.newFields.push(field);
                    }
                }
            }

            delete originalSchemaModel.fields;
            delete updatedSchemaModel.fields;

            if (_.isEqual(originalSchemaModel, updatedSchemaModel) === false) {
                updateSchemaModelData.schemaModel = updatedSchemaModel;
            }

            const updatedSchemaFieldIndexMapping: Record<number, number> = {};
            for (const fieldId in originalSchemaFieldDict) {
                if (!updatedSchemaFieldDict[fieldId]) {
                    updateSchemaModelData.deletedFields.push(originalSchemaFieldDict[fieldId]);
                } else if (_.isEqual(originalSchemaFieldDict[fieldId], updatedSchemaFieldDict[fieldId]) === false) {
                    updatedSchemaFieldIndexMapping[updateSchemaModelData.updatedFields.length] = updatedSchemaFieldIndexes[fieldId];
                    updateSchemaModelData.updatedFields.push(updatedSchemaFieldDict[fieldId]);
                }
            }

            if (this.props.schemaModel?.schemaId) {
                this.props.updateSchemaModel(this.props.schemaModel.schemaId, updateSchemaModelData, { newFieldIndexMappings, updatedSchemaFieldIndexMapping, fieldNamesByIndex }, afterSaveCallback);
            }
        } else {
            const newSchemaModel = _.cloneDeep(editedSchemaModel);
            if (newSchemaModel.fields && Array.isArray(newSchemaModel.fields)) {
                for (let index = 0; index < newSchemaModel.fields.length; index++) {
                    const field = _.cloneDeep(newSchemaModel.fields[index]);
                    newSchemaModel.fields[index] = field;
                    if (field.range) {
                        switch (field.dataType) {
                            case "datetime":
                            case "date":
                                field.min = field.range[0].format("YYYY-MM-DDTHH:mm:ss");
                                field.max = field.range[1].format("YYYY-MM-DDTHH:mm:ss");
                                delete field.range;
                                break;
                            case "time":
                                field.min = field.range[0].utc().valueOf();
                                field.max = field.range[1].utc().valueOf();
                                delete field.range;
                                break;
                            default:
                                break;
                        }
                    }
                }
            }
            this.props.createSchemaModel(newSchemaModel, afterSaveCallback);
        }
    };

    formatFormErrorHelp = (error: any) => {
        if (Array.isArray(error)) {
            return error.map((message, index) => <div key={`${message}-${index}`}>{message}</div>);
        }

        if (typeof error === "string" && error.includes("\n")) {
            return error.split("\n").map((message, index) => <div key={`${message}-${index}`}>{message}</div>);
        }

        return error;
    };

    getFormErrors = (fieldName: any) => {
        if (this.props.formErrors && this.props.formErrors[fieldName]) {
            return {
                help: this.formatFormErrorHelp(this.props.formErrors[fieldName]),
                validateStatus: "error",
            };
        }
        return this.emptyObject;
    };

    getFieldRowLevelError = (fieldIndex: any) => {
        if (this.props.formErrors) {
            const formErrors = this.props.formErrors;
            if (formErrors.fields && formErrors.fields[fieldIndex] && typeof formErrors.fields[fieldIndex] !== "string") {
                return formErrors.fields[fieldIndex];
            }

            const fieldErrorPrefix = `fields.${fieldIndex}.`;
            const flatFieldErrors = Object.keys(formErrors).reduce((errors: any, fieldName: string) => {
                if (fieldName.startsWith(fieldErrorPrefix)) {
                    errors[fieldName.slice(fieldErrorPrefix.length)] = formErrors[fieldName];
                }
                return errors;
            }, {});

            if (Object.keys(flatFieldErrors).length > 0) {
                return flatFieldErrors;
            }
        }
        return null;
    };

    isReadOnlyView = () => {
        return this.state.readOnlyView || !(this.props.permission.canEdit || this.props.permission.canAdd);
    };

    isSaveAllowed = () => {
        if (this.props.action === "edit") {
            return this.props.permission.canEdit;
        } else if (this.props.action === "create") {
            return this.props.permission.canAdd;
        }
        return false;
    };

    onAutoTagClick = () => {
        if (this.state.isAutoTagging || this.state.isFetchingTags || this.state.isAutoTagRequested) {
            return;
        }
        this.setState({ isFetchingTags: true, autoTagError: false, isAutoTagRequested: true });

        const businessAreaId =
            this.props.userBusinessAreas && Object.keys(this.props.userBusinessAreas).length > 0
                ? this.props.userBusinessAreas[Object.keys(this.props.userBusinessAreas)[0]].businessAreaId
                : this.state.businessAreaId;

        !!businessAreaId && this.props.getDataTagsByBusinessAreaId(businessAreaId);
    };

    triggerAutoTagging = () => {
        this.onAutoTagClick();
    };
    onApplyAutoTagSuggestions = (selectedSuggestions: any) => {
        if (this.formComponentRef.current) {
            console.log("Applying auto-tag suggestions: ", selectedSuggestions);
            this.formComponentRef.current.onApplyAutoTagSuggestions(selectedSuggestions);
        } else {
            console.error("ManageSchemaModelForm is missing from the tree.");
        }
    };

    showCommentsModal = () => {
        this.setState({ isCommentsModalVisible: true });
    };

    closeCommentsModal = () => {
        this.setState({ isCommentsModalVisible: false });
    };

    showAnnotationsModal = () => {
        this.setState({ isAnnotationsModalVisible: true });
    };

    closeAnnotationsModal = () => {
        this.setState({ isAnnotationsModalVisible: false });
    };

    getResolvedBusinessAreaId = () => {
        return (
            this.state.businessAreaId ||
            this.props.schemaModel?.businessAreaId ||
            (this.props.userBusinessAreas &&
                Object.keys(this.props.userBusinessAreas).length > 0 &&
                this.props.userBusinessAreas[Object.keys(this.props.userBusinessAreas)[0]]?.businessAreaId)
        );
    };

    showHealthReportModal = () => {
        this.setState({ isHealthReportModalVisible: true });
        const schemaId = this.props.schemaModel?.schemaId;
        const businessAreaId = this.getResolvedBusinessAreaId();
        if (schemaId && businessAreaId) {
            this.props.generateSchemaHealthReport(schemaId, businessAreaId, false);
        }
    };

    closeHealthReportModal = () => {
        this.setState({ isHealthReportModalVisible: false });
    };

    regenerateHealthReport = () => {
        const schemaId = this.props.schemaModel?.schemaId;
        const businessAreaId = this.getResolvedBusinessAreaId();
        if (schemaId && businessAreaId) {
            this.props.generateSchemaHealthReport(schemaId, businessAreaId, true);
        }
    };

    showPIISuggestionsModal = () => {
        const schemaId = this.props.schemaModel?.schemaId;
        const businessAreaId = this.getResolvedBusinessAreaId();
        if (schemaId && businessAreaId) {
            this.setState({ isPIISuggestionsModalVisible: true });
            this.props.getPiiSuggestions(schemaId, businessAreaId);
        } else {
            message.error("Schema must be saved before analyzing PII.");
        }
    };

    regeneratePiiSuggestions = () => {
        const schemaId = this.props.schemaModel?.schemaId;
        const businessAreaId = this.getResolvedBusinessAreaId();
        if (schemaId && businessAreaId) {
            this.props.getPiiSuggestions(schemaId, businessAreaId);
        }
    };

    closePIISuggestionsModal = () => {
        this.setState({ isPIISuggestionsModalVisible: false, isPiiSuggestionsSaveRequested: false });
        this.props.clearPiiSuggestions();
    };

    onSavePiiSuggestions = (businessAreaId: string, schemaId: string, updatedFields: any[]) => {
        // Apply PII suggestions to the form instead of saving directly to DB
        const formRef = this.formComponentRef?.current;
        if (formRef && typeof formRef.applyAiPiiSuggestions === "function") {
            formRef.applyAiPiiSuggestions(updatedFields);
            this.setState({ isPIISuggestionsModalVisible: false, isPiiSuggestionsSaveRequested: false });
            this.props.clearPiiSuggestions();
        } else {
            message.error("Unable to apply PII suggestions. Form reference not available.");
        }
    };

    showFieldDescriptionsModal = () => {
        const schemaId = this.props.schemaModel?.schemaId;
        const businessAreaId = this.getResolvedBusinessAreaId();
        if (schemaId && businessAreaId) {
            this.setState({ isFieldDescriptionsModalVisible: true });
            this.props.getFieldDescriptions(schemaId, businessAreaId);
        } else {
            message.error("Schema must be saved before generating field descriptions.");
        }
    };

    regenerateFieldDescriptions = () => {
        const schemaId = this.props.schemaModel?.schemaId;
        const businessAreaId = this.getResolvedBusinessAreaId();
        if (schemaId && businessAreaId) {
            this.props.getFieldDescriptions(schemaId, businessAreaId);
        }
    };

    closeFieldDescriptionsModal = () => {
        this.setState({ isFieldDescriptionsModalVisible: false, isFieldDescriptionsSaveRequested: false });
        this.props.clearFieldDescriptions();
    };

    showDdlSqlModal = () => {
        const schemaId = this.props.schemaModel?.schemaId;
        const businessAreaId = this.getResolvedBusinessAreaId();
        if (schemaId && businessAreaId) {
            this.setState({ isDdlSqlModalVisible: true });
            this.props.generateDdlSql(schemaId, businessAreaId);
        } else {
            message.error("Schema must be saved before generating DDL SQL.");
        }
    };

    closeDdlSqlModal = () => {
        this.setState({ isDdlSqlModalVisible: false });
        this.props.clearDdlSql();
    };

    showGapPredictionModal = () => {
        const schemaId = this.props.schemaModel?.schemaId;
        const businessAreaId = this.getResolvedBusinessAreaId();
        if (schemaId && businessAreaId) {
            this.setState({ isGapPredictionModalVisible: true });
            this.props.generateGapPrediction(schemaId, businessAreaId, false);
        } else {
            message.error("Schema must be saved before predicting gaps.");
        }
    };

    regenerateGapPrediction = () => {
        const schemaId = this.props.schemaModel?.schemaId;
        const businessAreaId = this.getResolvedBusinessAreaId();
        if (schemaId && businessAreaId) {
            this.props.generateGapPrediction(schemaId, businessAreaId, true);
        }
    };

    closeGapPredictionModal = () => {
        this.setState({ isGapPredictionModalVisible: false });
        this.props.clearGapPrediction();
    };

    onSaveFieldDescriptions = (businessAreaId: string, schemaId: string, updatedFields: any[], schemaDescription: string) => {
        // Apply field descriptions to the form instead of saving directly to DB
        const formRef = this.formComponentRef?.current;
        if (formRef && typeof formRef.applyAiFieldDescriptions === "function") {
            formRef.applyAiFieldDescriptions(updatedFields, schemaDescription);
            this.setState({ isFieldDescriptionsModalVisible: false, isFieldDescriptionsSaveRequested: false });
            this.props.clearFieldDescriptions();
        } else {
            message.error("Unable to apply field descriptions. Form reference not available.");
        }
    };

    render() {
        const { schemaModel, userCommentsPermission, action } = this.props;
        const autoTagButton = {
            onClick: this.onAutoTagClick,
            disabled: this.isReadOnlyView() || !this.props.keyExists?.exists,
            disabledTooltipTitle: !this.props.keyExists?.exists
                ? "OpenAI API key is not configured. Please add it in System Keys to enable auto-tagging functionality."
                : "Read-only view",
            onAutoTagging: (payload: any) => {
                this.setState({ isAutoTagging: true, autoTagError: false, isAutoTagRequested: false });
                this.props.autoTagging(payload);
            },
            onAutoTagCancel: () => {
                this.setState({ isAutoTagRequested: false, isFetchingTags: false, isAutoTagging: false });
            },
        };
        return (
            <>
                <SchemaHealthReportModal
                    open={this.state.isHealthReportModalVisible}
                    onClose={this.closeHealthReportModal}
                    reportHtml={this.props.schemaHealthReportHtml}
                    loading={this.props.generateSchemaHealthReportResult?.loading}
                    error={this.props.generateSchemaHealthReportResult?.success === false}
                    onRegenerate={this.regenerateHealthReport}
                />
                <PIISuggestionsModal
                    visible={this.state.isPIISuggestionsModalVisible}
                    loading={this.props.getPiiSuggestionsResult?.loading}
                    savingPii={this.props.savePiiSuggestionsResult?.loading}
                    piiSuggestionsData={this.props.piiSuggestionsData}
                    onCancel={this.closePIISuggestionsModal}
                    onRegenerate={this.regeneratePiiSuggestions}
                    onSave={this.onSavePiiSuggestions}
                />
                <FieldDescriptionsModal
                    visible={this.state.isFieldDescriptionsModalVisible}
                    loading={this.props.getFieldDescriptionsResult?.loading}
                    saving={this.props.saveFieldDescriptionsResult?.loading}
                    fieldDescriptionsData={this.props.fieldDescriptionsData}
                    onCancel={this.closeFieldDescriptionsModal}
                    onRegenerate={this.regenerateFieldDescriptions}
                    onSave={this.onSaveFieldDescriptions}
                />
                <DDLSqlExportModal
                    visible={this.state.isDdlSqlModalVisible}
                    onClose={this.closeDdlSqlModal}
                    generateDdlSqlResult={this.props.generateDdlSqlResult}
                    ddlSqlData={this.props.ddlSqlData}
                />
                <GapPredictionModal
                    visible={this.state.isGapPredictionModalVisible}
                    onClose={this.closeGapPredictionModal}
                    generateGapPredictionResult={this.props.generateGapPredictionResult}
                    gapPredictionData={this.props.gapPredictionData}
                    onRegenerate={this.regenerateGapPrediction}
                />
                <Modal
                    title="Comments"
                    open={this.state.isCommentsModalVisible}
                    onCancel={this.closeCommentsModal}
                    footer={null}
                    width={800}
                    styles={{
                        body: { height: "60vh", overflow: "auto" },
                    }}
                >
                    {schemaModel?.schemaId && <Comments key={schemaModel.schemaId} recordId={schemaModel.schemaId} permission={userCommentsPermission} recordType="schema" />}
                </Modal>
                <Modal
                    title="Annotations"
                    open={this.state.isAnnotationsModalVisible}
                    onCancel={this.closeAnnotationsModal}
                    footer={null}
                    width={900}
                    styles={{
                        body: { height: "70vh", overflow: "auto" },
                    }}
                >
                    {schemaModel?.schemaId && (
                        <Annotations
                            key={`${schemaModel.schemaId}-schema-annotations`}
                            recordId={schemaModel.schemaId}
                            recordType="schema"
                            permission={userCommentsPermission}
                            fields={this.props.schemaModel?.fields || []}
                            businessAreaId={this.state.businessAreaId}
                        />
                    )}
                </Modal>
                <ManageSchemaModelForm
                    ref={this.formComponentRef}
                    autoTaggingData={this.props.autoTaggingData}
                    schemaModel={this.getCurrentSchemaModel()}
                    nameRules={nameRules}
                    descriptionRules={descriptionRules}
                    readOnly={this.isReadOnlyView()}
                    allowSave={this.isSaveAllowed()}
                    businessAreaDataList={this.props.businessAreaDataList}
                    getBusinessAreaDataForBusinessAreaResult={this.props.getBusinessAreaDataForBusinessAreaResult}
                    userCommentsPermission={this.props.userCommentsPermission}
                    openFieldCommentId={this.state.openFieldCommentId ?? undefined}
                    openFieldAnnotationId={this.state.openFieldAnnotationId ?? undefined}
                    onSwitchEditMode={this.props.onSwitchEditMode}
                    onFinish={this.onFormSubmit}
                    formErrors={this.props.formErrors}
                    getFormErrors={this.getFormErrors}
                    onCancel={this.props.onCancel}
                    onExportSchema={this.props.exportSchema}
                    getFieldRowLevelError={this.getFieldRowLevelError}
                    autoTagButton={autoTagButton}
                    getDataTagsByBusinessAreaIdResult={this.props.getDataTagsByBusinessAreaIdResult}
                    dataTagsByBusinessAreaIdResult={this.props.dataTagsByBusinessAreaIdResult}
                    autoTaggingResult={this.props.autoTaggingResult}
                    isAutoTagging={this.state.isAutoTagging}
                    isFetchingTags={this.state.isFetchingTags}
                    isAutoTagRequested={this.state.isAutoTagRequested}
                    autoTagError={this.state.autoTagError}
                    header={
                        action !== "create" &&
                        userCommentsPermission?.canView && (
                            <div className="flex flex-initial items-end gap-2">
                                <Space.Compact>
                                    <Tooltip title="Show Comments">
                                        <Button type="default" icon={<CommentOutlined />} onClick={this.showCommentsModal} title="Show Comments" />
                                    </Tooltip>
                                    <Tooltip title="Show Annotations">
                                        <Button type="default" icon={<HighlightOutlined />} onClick={this.showAnnotationsModal} title="Show Annotations" />
                                    </Tooltip>
                                </Space.Compact>
                            </div>
                        )
                    }
                    footer={<></>}
                />
            </>
        );
    }
}

const mapStateToProps = (state: any) => ({
    getCommentsPermissionResult: state.comments.getCommentsPermissionResult,
    userCommentsPermission: state.comments.userCommentsPermission,
    getDataTagsByBusinessAreaIdResult: state.dataTags.getDataTagsByBusinessAreaIdResult,
    userBusinessAreas: state.schemaModels.userBusinessAreas,
    generateSchemaHealthReportResult: state.schemaModels.generateSchemaHealthReportResult,
    schemaHealthReportHtml: state.schemaModels.schemaHealthReportHtml,
    getPiiSuggestionsResult: state.schemaModels.getPiiSuggestionsResult,
    piiSuggestionsData: state.schemaModels.piiSuggestionsData,
    savePiiSuggestionsResult: state.schemaModels.savePiiSuggestionsResult,
    getFieldDescriptionsResult: state.schemaModels.getFieldDescriptionsResult,
    fieldDescriptionsData: state.schemaModels.fieldDescriptionsData,
    saveFieldDescriptionsResult: state.schemaModels.saveFieldDescriptionsResult,
    generateDdlSqlResult: state.schemaModels.generateDdlSqlResult,
    ddlSqlData: state.schemaModels.ddlSqlData,
    generateGapPredictionResult: state.schemaModels.generateGapPredictionResult,
    gapPredictionData: state.schemaModels.gapPredictionData,
});

const mapDispatchToProps = (dispatch: any) => ({
    getCommentsPermission: () => dispatch(actions.getCommentsPermissionRequest()),
    getDataTagsByBusinessAreaId: (businessAreaId: string) => dispatch(actions.getDataTagsByBusinessAreaIdRequest(businessAreaId)),
    generateSchemaHealthReport: (schemaId: string, businessAreaId: string, regenerate: boolean) =>
        dispatch(actions.generateSchemaHealthReportRequest(schemaId, businessAreaId, regenerate)),
    getPiiSuggestions: (schemaId: string, businessAreaId: string) => dispatch(actions.getPiiSuggestionsRequest(schemaId, businessAreaId)),
    savePiiSuggestions: (businessAreaId: string, schemaId: string, updatedFields: any[]) => dispatch(actions.savePiiSuggestionsRequest(businessAreaId, schemaId, updatedFields)),
    clearPiiSuggestions: () => dispatch(actions.clearPiiSuggestions()),
    getFieldDescriptions: (schemaId: string, businessAreaId: string) => dispatch(actions.getFieldDescriptionsRequest(schemaId, businessAreaId)),
    saveFieldDescriptions: (businessAreaId: string, schemaId: string, updatedFields: any[], schemaDescription: string) =>
        dispatch(actions.saveFieldDescriptionsRequest(businessAreaId, schemaId, updatedFields, schemaDescription)),
    clearFieldDescriptions: () => dispatch(actions.clearFieldDescriptions()),
    generateDdlSql: (schemaId: string, businessAreaId: string) => dispatch(actions.generateDdlSqlRequest(schemaId, businessAreaId)),
    clearDdlSql: () => dispatch(actions.clearDdlSql()),
    generateGapPrediction: (schemaId: string, businessAreaId: string, regenerate: boolean = false) =>
        dispatch(actions.generateGapPredictionRequest(schemaId, businessAreaId, regenerate)),
    clearGapPrediction: () => dispatch(actions.clearGapPrediction()),
});

export default connect(mapStateToProps, mapDispatchToProps, null, { forwardRef: true })(ManageSchemaModel);
